import { ChangeDetectionStrategy, Component, inject, OnDestroy } from "@angular/core";
import { MatDialog, MatDialogConfig } from "@angular/material/dialog";
import { MatTableDataSource } from "@angular/material/table";
import { FieldArrayType, FieldArrayTypeConfig } from "@ngx-formly/core";
import { Store } from "@ngxs/store";
import { OrganizationState } from "@vp/data-access/organization";
import { TagsState } from "@vp/data-access/tags";
import {
  FeeBasedService,
  Organization,
  PricingPlanOverride,
  ServiceCost,
  Tag,
  TagType
} from "@vp/models";
import { ConfirmationDialogComponent } from "@vp/shared/components/confirmation-dialog";
import { FeatureConfig, FeatureService } from "@vp/shared/features";
import { NotificationService } from "@vp/shared/notification-service";
import { filterNullMap } from "@vp/shared/operators";
import * as TagManagerActions from "@vp/tag-manager/data-access/tag-manager-state";
import { TagManagerState } from "@vp/tag-manager/data-access/tag-manager-state";
import { AddCostPricingPlanDialogComponent } from "@vp/tag-manager/ui/add-cost-pricing-plan";
import { AddServiceFeeOverrideDialogComponent } from "@vp/tag-manager/ui/add-service-fee-override";
import { AddNewSpecialtyDialogComponent } from "libs/tag-manager/ui/add-specialty/src";
import { BehaviorSubject, combineLatest, Observable, of, Subject } from "rxjs";
import { map, shareReplay, switchMap, take, takeUntil, tap } from "rxjs/operators";

const FEE_BASED_SERVICE_FEATURE_ID = "feeBasedServices";

export interface EditServiceCostPlanModel {
  friendlyId?: string;
  active?: boolean;
  fee?: number;
}

// export interface OverrideCostPlanViewModel {
//   isDefault: boolean;
//   planName: string;
//   serviceCosts: EditServiceCostPlanModel[];
//   specialties: string[];
// }

@Component({
  selector: "vp-edit-service-cost-plan",
  standalone: false,
  templateUrl: "./edit-service-cost-plan.component.html",
  styleUrls: ["./edit-service-cost-plan.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class EditServiceCostPlanComponent
  extends FieldArrayType<FieldArrayTypeConfig>
  implements OnDestroy
{
  tags$: Observable<Tag[]> = inject(Store).select(TagsState.tags);
  organization$: Observable<Organization | null> = inject(Store).select(
    OrganizationState.organization
  );
  tagState$: Observable<Tag | null> = inject(Store).select(TagsState.tagState);
  selectedTagType$: Observable<TagType | null> = inject(Store).select(
    TagManagerState.selectedTagType
  );
  selectedTag$: Observable<Tag | null> = inject(Store).select(TagManagerState.selectedTag);

  private readonly destroy$ = new Subject<void>();
  private readonly dataSource$$ =
    new BehaviorSubject<MatTableDataSource<PricingPlanOverride> | null>(null);

  constructor(
    private readonly notificationService: NotificationService,
    private readonly matDialog: MatDialog,
    private readonly store: Store,
    private readonly featureService: FeatureService
  ) {
    super();
  }

  dataSource$ = this.dataSource$$.asObservable();

  feature$: Observable<FeatureConfig | null> = this.featureService.feature$(
    FEE_BASED_SERVICE_FEATURE_ID
  );

  allFeeBasedServices$ = this.feature$.pipe(
    filterNullMap(),
    map(featureConfig => {
      return featureConfig?.feeBasedServices ?? [];
    })
  );

  feeBasedServicesEnabled$ = this.feature$.pipe(
    map(featureConfig => {
      return featureConfig?.enabled ?? false;
    }),
    shareReplay()
  );

  serviceCostOverrides$ = this.selectedTag$.pipe(
    filterNullMap(),
    map((tag: Tag) => {
      if (
        tag.tagData &&
        Object.keys(tag.tagData).includes("serviceCostOverrides") &&
        Array.isArray(tag.tagData.serviceCostOverrides)
      ) {
        return tag.tagData.serviceCostOverrides as PricingPlanOverride[];
      }
      return [];
    }),
    takeUntil(this.destroy$)
  );

  feeBasedServiceLookup$ = (friendlyId: string): Observable<FeeBasedService> =>
    this.allFeeBasedServices$.pipe(
      map((allFeeBasedServices: FeeBasedService[]) => {
        const found = allFeeBasedServices.find(
          feeBasedService => feeBasedService.friendlyId === friendlyId
        );
        if (found) {
          return found;
        } else {
          return {
            friendlyId: friendlyId,
            displayName: `[ ${friendlyId} ]`
          } as FeeBasedService;
        }
      }),
      take(1)
    );

  subTable$(costPlan: PricingPlanOverride[]): Observable<PricingPlanOverride[]> {
    return of(costPlan);
  }

  addPlan() {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((tag: Tag) =>
          combineLatest([
            of(tag),
            this.matDialog
              .open(AddCostPricingPlanDialogComponent, {
                disableClose: true,
                width: "30vw",
                data: {
                  mode: "add"
                },
                panelClass: "mat-mdc-dialog-basic"
              } as MatDialogConfig)
              .afterClosed()
              .pipe(filterNullMap())
          ])
        ),
        filterNullMap()
      )
      .subscribe({
        next: ([tag, planName]: [Tag, string]): void => {
          this.store.dispatch(new TagManagerActions.AddServiceCostPricingPlan(tag.tagId, planName));
        }
      });
  }

  updatePlanName = (override: PricingPlanOverride) => {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((tag: Tag) =>
          combineLatest([
            of(tag),
            of(override),
            this.matDialog
              .open(AddCostPricingPlanDialogComponent, {
                disableClose: true,
                width: "30vw",
                data: {
                  planName: override.planName,
                  mode: "edit"
                },
                panelClass: "mat-mdc-dialog-basic"
              } as MatDialogConfig)
              .afterClosed()
              .pipe(filterNullMap())
          ])
        ),
        switchMap(([tag, override, newPlanName]: [Tag, PricingPlanOverride, string]) =>
          this.store.dispatch(
            new TagManagerActions.PatchPricingPlanOverride(tag.tagId, override.planName, {
              planName: newPlanName
            })
          )
        )
      )
      .subscribe();
  };

  changeDefaultPlan = (override: PricingPlanOverride) => {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((tag: Tag) => combineLatest([of(tag), of(override)])),
        switchMap(([tag, override]: [Tag, PricingPlanOverride]) =>
          this.store.dispatch(
            new TagManagerActions.PatchPricingPlanOverride(tag.tagId, override.planName, {
              isDefault: true
            })
          )
        )
      )
      .subscribe();
  };

  deletePlan = (override: PricingPlanOverride, selectedTag: Tag) => {
    const mes = `Are you sure you want to delete "${override.planName}" and all services associated?`;
    this.matDialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: "Are you sure?",
          message: mes
        },
        panelClass: "mat-mdc-dialog-confirm"
      })
      .afterClosed()
      .pipe(
        filterNullMap(),
        take(1),
        tap((confirmed: boolean) => {
          if (confirmed) {
            this.store.dispatch(
              new TagManagerActions.DeletePlan(selectedTag.tagId, override.planName)
            );
            this.notificationService.successMessage("Deleted successfully");
          }
        })
      )
      .subscribe({
        error: e => {
          if (e.status === 403) {
            this.notificationService.errorMessage(
              "You do not have permission to delete pricing plans"
            );
            return;
          }
          this.notificationService.errorMessage("Delete failed.");
        }
      });
  };

  addServiceFee = (overridePlan: PricingPlanOverride) => {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((tag: Tag) =>
          combineLatest([
            of(tag),
            of(overridePlan),
            this.matDialog
              .open(AddServiceFeeOverrideDialogComponent, {
                disableClose: true,
                width: "30vw",
                data: {
                  planName: overridePlan.planName,
                  existingServices: overridePlan.serviceCosts,
                  mode: "add"
                },
                panelClass: "mat-mdc-dialog-basic"
              } as MatDialogConfig)
              .afterClosed()
              .pipe(filterNullMap())
          ])
        ),
        switchMap(([tag, override, serviceCost]: [Tag, PricingPlanOverride, ServiceCost]) =>
          this.store.dispatch(
            new TagManagerActions.AddServiceCost(tag.tagId, override.planName, serviceCost)
          )
        )
      )
      .subscribe();
  };

  editServiceFee = (overridePlan: PricingPlanOverride, serviceFee: ServiceCost) => {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((tag: Tag) =>
          combineLatest([
            of(tag),
            of(overridePlan),
            this.matDialog
              .open(AddServiceFeeOverrideDialogComponent, {
                disableClose: true,
                width: "30vw",
                data: {
                  ...serviceFee,
                  planName: overridePlan.planName,
                  mode: "edit"
                },
                panelClass: "mat-mdc-dialog-basic"
              } as MatDialogConfig)
              .afterClosed()
              .pipe(filterNullMap())
          ])
        ),
        tap(([tag, override, serviceCost]: [Tag, PricingPlanOverride, ServiceCost]) => {
          this.store.dispatch(
            new TagManagerActions.EditServiceCost(tag.tagId, override.planName, serviceCost)
          );
        })
      )
      .subscribe();
  };

  deleteServiceFee = (
    overridePlan: PricingPlanOverride,
    serviceFee: ServiceCost,
    selectedTag: Tag
  ) => {
    const mes = `Are you sure you want to delete the service "${serviceFee.friendlyId}" from this pricing plan?`;
    this.matDialog
      .open(ConfirmationDialogComponent, {
        data: {
          title: "Are you sure?",
          message: mes
        },
        panelClass: "mat-mdc-dialog-confirm"
      })
      .afterClosed()
      .pipe(
        filterNullMap(),
        take(1),
        tap((confirmed: boolean) => {
          if (confirmed) {
            this.store.dispatch(
              new TagManagerActions.DeleteServiceCost(
                selectedTag.tagId,
                overridePlan.planName,
                serviceFee.friendlyId
              )
            );
            this.notificationService.successMessage("Deleted successfully");
          }
        })
      )
      .subscribe({
        error: e => {
          if (e.status === 403) {
            this.notificationService.errorMessage(
              "You do not have permission to delete service fees"
            );
            return;
          }
          this.notificationService.errorMessage("Delete failed.");
        }
      });
  };

  addSpecialty(planName: string) {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap((tag: Tag) =>
          combineLatest([
            of(tag),
            this.matDialog
              .open(AddNewSpecialtyDialogComponent, {
                disableClose: true,
                width: "30vw",
                data: {
                  tagData: tag.tagData
                }
              } as MatDialogConfig)
              .afterClosed()
              .pipe(filterNullMap())
          ])
        ),
        switchMap(([tag, specialties]: [Tag, string[]]) => {
          return this.store.dispatch(
            new TagManagerActions.AddSpecialties(tag.tagId, planName, specialties)
          );
        })
      )
      .subscribe();
  }

  deleteSpecialty(planName: string, specialtyTagId: string) {
    this.selectedTag$
      .pipe(
        filterNullMap(),
        take(1),
        switchMap(tag =>
          this.store.dispatch(
            new TagManagerActions.DeleteSpecialty(tag.tagId, planName, specialtyTagId)
          )
        )
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
